/**
 * The two buttons the brand defines (AXAI-BRAND-001 §6).
 *
 *   Primary   ink fill, paper text, 700.
 *   Secondary 2px ink border, surface fill, ink text.
 *
 * Radius 8 (control), 44px minimum target, and the focus ring comes from globals.css so it can
 * never be removed here.
 *
 * A thing that navigates renders an `<a>`; a thing that acts renders a `<button>`. That is the
 * accessibility gate in the brief ("real buttons and links"), so the choice is made by which
 * prop is passed, not by a `variant` string someone can get wrong: pass `href` and you get a
 * link, pass `onClick` and you get a button.
 */

import Link from 'next/link';
import type { ComponentProps, ReactNode } from 'react';

type Variant = 'primary' | 'secondary';

const BASE =
  'inline-flex min-h-touch items-center justify-center gap-2 rounded-control px-[26px] py-4 text-body font-bold no-underline';

const VARIANT: Record<Variant, string> = {
  primary: 'bg-ink text-onInk',
  secondary: 'border-2 border-ink bg-surface text-ink',
};

/** A link that looks like a button. Internal hrefs go through next/link. */
export function ButtonLink({
  href,
  variant = 'primary',
  className = '',
  children,
  ...rest
}: {
  href: string;
  variant?: Variant;
  className?: string;
  children: ReactNode;
} & Omit<ComponentProps<'a'>, 'href' | 'className' | 'children'>) {
  const cls = `${BASE} ${VARIANT[variant]} ${className}`;
  // mailto:, tel: and external links are plain anchors. External links open in the same tab by
  // default (AXAI-SITE-001 §3), so this component sets no target. The spec's one exception - a
  // link to a partner or co-founded venture's own site - passes target and rel through `rest`
  // at the call site, which is how "Visit dsx.org.au" on /dsx works. Do not add a target here:
  // the default is still the same tab, and the exception is per-link and deliberate.
  const isInternal = href.startsWith('/') || href.startsWith('#');
  if (!isInternal) {
    return (
      <a href={href} className={cls} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <Link href={href} className={cls} {...rest}>
      {children}
    </Link>
  );
}

/** A real button, for something that acts rather than navigates. */
export function Button({
  variant = 'primary',
  className = '',
  children,
  ...rest
}: {
  variant?: Variant;
  className?: string;
  children: ReactNode;
} & Omit<ComponentProps<'button'>, 'className' | 'children'>) {
  return (
    <button type="button" className={`${BASE} ${VARIANT[variant]} ${className}`} {...rest}>
      {children}
    </button>
  );
}
